import type { ICard } from "@components/cards/Card";

import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";
import { CardLink } from "@components/cards/Card";
import { sizes } from "@ui/lib/sizes";
import { clsx } from "clsx";
import NextImage from "next/image";
import { Noise } from "@ui/components/core/Noise/Noise";
import { getImageProps } from "@utilities/payload";
import { Media } from "@mw/shared/payload-types";

export interface ICardCaseStudy extends ICard {
  category: string | React.ReactNode;
  icon: React.ReactNode;
  iconHover?: React.ReactNode;
  arrowIcon: React.ReactNode;
  description: string | React.ReactNode;
  href?: string | null;
  className?: string;
  backgroundImage?: Media | string | null;
  noiseOpacity?: number;
}

export function CardCaseStudy({
  category,
  icon,
  iconHover,
  arrowIcon,
  description,
  href,
  className,
  backgroundImage,
  tag = "div",
  noiseOpacity = 0.5,
  ...props
}: ICardCaseStudy) {
  const backgroundImageProps = getImageProps(backgroundImage, { fill: true });
  const Tag = tag;

  return (
    <Tag
      className={clsx(
        "tw-relative tw-isolate tw-w-full tw-aspect-square tw-rounded-2xl tw-bg-cream tw-overflow-hidden",
        (href || !!backgroundImageProps) && "tw-group",
        href && "tw-cursor-pointer has-[:focus-visible]:tw-outline-focus",
        className,
      )}
      {...props}
    >
      {/* Background image on hover */}
      {backgroundImageProps && (
        <div className='tw-absolute tw-z-0 tw-inset-0 tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-100'>
          <NextImage
            {...backgroundImageProps}
            alt=''
            className='tw-object-cover tw-object-center'
            sizes={sizes("100vw", { md: "50vw", xl: "min(33.3vw, 26rem)" })}
          />
          <Noise style={{ opacity: noiseOpacity }} />
        </div>
      )}

      {/* Circular arrow indicator - top right (not clickable, just visual) */}
      {href && (
        <div
          className={clsx(
            "tw-absolute tw-z-10 tw-top-2 tw-right-2 tw-transition-opacity tw-duration-300 tw-pointer-events-none",
            "tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-full",
            "tw-bg-white",
            "tw-opacity-0 group-hover:tw-opacity-100",
          )}
          style={{
            boxShadow: "0 0 1px 0 rgba(0,0,0,0.40), 0 1px 2px 0 rgba(0,0,0,0.04), 0 2px 2px 0 rgba(0,0,0,0.04)",
          }}
          aria-hidden='true'
        >
          {arrowIcon}
        </div>
      )}

      {/* Icon - absolutely centered */}
      <div
        className={clsx(
          "tw-absolute tw-z-10 tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-pointer-events-none",
          !!backgroundImageProps
            ? "tw-text-black group-hover:tw-text-white tw-transition-colors tw-duration-300"
            : "tw-text-black",
        )}
      >
        {/* If iconHover is provided, show both icons with visibility toggle */}
        {iconHover ? (
          <>
            <div className='tw-transition-opacity tw-duration-300 group-hover:tw-opacity-0'>{icon}</div>
            <div className='tw-absolute tw-inset-0 tw-opacity-0 tw-transition-opacity tw-duration-300 group-hover:tw-opacity-100'>
              {iconHover}
            </div>
          </>
        ) : (
          /* Otherwise apply filter to the single icon if backgroundImage exists */
          <div
            className={clsx(
              !!backgroundImageProps &&
                "[&_img]:tw-transition-[filter] [&_img]:tw-duration-300 [&_img]:group-hover:tw-brightness-0 [&_img]:group-hover:tw-invert",
            )}
          >
            {icon}
          </div>
        )}
      </div>

      <div className='tw-relative tw-z-10 tw-h-full tw-flex tw-flex-col tw-p-8 tw-gap-6'>
        {/* Category tag with circle indicator */}
        {category && (
          <div className='tw-flex tw-items-center tw-gap-2 tw-shrink-0'>
            {typeof category === "string" && (
              <div
                className={clsx(
                  "tw-size-2.5 tw-rounded-full tw-opacity-60 tw-border",
                  !!backgroundImageProps
                    ? "tw-border-black group-hover:tw-border-white tw-transition-colors tw-duration-300"
                    : "tw-border-black",
                )}
              />
            )}
            <p
              className={clsx(
                "tw-opacity-60 tw-type-xs",
                !!backgroundImageProps
                  ? "tw-text-black group-hover:tw-text-white group-hover:tw-opacity-80 tw-transition-colors tw-duration-300"
                  : "tw-text-black",
              )}
            >
              {category}
            </p>
          </div>
        )}

        {/* Spacer to push description to bottom */}
        <div className='tw-grow' />

        {/* Description text */}
        <div className='tw-shrink-0 tw-z-10'>
          {(() => {
            const descriptionContent =
              typeof description === "string" ? (
                <p
                  className={clsx(
                    "tw-text-base tw-leading-[1.375rem] tw-line-clamp-2 xl:tw-line-clamp-3",
                    !!backgroundImageProps
                      ? "tw-text-black group-hover:tw-text-white tw-transition-colors tw-duration-300"
                      : "tw-text-black",
                  )}
                >
                  {description}
                </p>
              ) : (
                description
              );

            return href ? (
              <CardLink href={href} data-card-link className='tw-outline-none'>
                {descriptionContent}
              </CardLink>
            ) : (
              descriptionContent
            );
          })()}
        </div>

        <HairlineCardBorder />
      </div>
    </Tag>
  );
}
